import useCompanyInfo from '@/composables/logged-in-user-account/company-info';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import useLoggedInUserAccount from '@/composables/logged-in-user-account/logged-in-user-account';
import useLogin from '@/composables/user/login';
import useMe from '@/composables/me';
import useSnackBar from '@/composables/snackbar';
import { EVENTS } from '@/lib/constants';
import { EventBus } from '@/lib/event-bus';
import { GATE, gateDecision } from '@/composables/app-start/gate';
import { discoverTenant } from '@/composables/app-start/tenant-discovery';
import { doFindAccount } from '@/api';
import { fetchMe } from '@/api/sentinel';
import { healTarget } from '@/composables/app-start/heal';
import { isAuthenticatedRouteName, setDocumentTitle } from '@/router';
import { ref } from 'vue';
import { routeName } from 'shared/constants.json';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';

// useAppStart is the login/onboarding/tenant-resolution state machine (the
// one-shot bootstrap). It owns the decisions; the fetch primitives
// (preInitializeUser / initializeUser) live in useLogin and are decision-free.
// The reactive, always-on URL healing is a separate app-shell concern in
// useRouteHealer; both share the discoverTenant + healTarget primitives.
export default function useAppStart(pageVisitsService) {
  const route = useRoute();
  const router = useRouter();
  const store = useStore();
  const loading = ref(false);

  const { loggedInUser, activateMembership } = useLoggedInUser();
  const { loggedInUserAccount } = useLoggedInUserAccount();
  const { showCompanyInfoInOnboarding } = useCompanyInfo();
  const { preInitializeUser, initializeUser } = useLogin();
  const { reachableAccounts, lastActiveAccount } = useMe();
  const snackbar = useSnackBar();
  const { t } = useI18n();

  const findLastPage = () => {
    const page = pageVisitsService.getLastPageVisit();
    if (page === null || page.routeName === routeName.logout || !routeName.all.includes(page.routeName)) {
      return { ok: false };
    }
    return { ok: true, page };
  };

  const restoreLastPage = () => {
    pageVisitsService.fetchPageVisits({ user: loggedInUser.value });

    if (route.name !== routeName.home || Object.keys(route.query).length > 0) {
      return Promise.resolve();
    }
    const { page, ok } = findLastPage();
    if (!ok) {
      return Promise.resolve();
    }
    return router.replace({ name: page.routeName, params: page.params, query: page.query });
  };

  // CHECK_SESSION — /me is the probe (200 ⇒ session, 401 ⇒ ANON) and the account
  // source. Stores the /me snapshot (so useMe reflects it) and resolves it, or
  // null (marking the store logged-out) for ANON.
  const checkSession = () => fetchMe().then((response) => {
    if (response?.status !== 200) {
      store.commit('USER_NOT_LOGGED_IN');
      return null;
    }
    store.commit('ME_RETRIEVED', response.data);
    return response.data;
  });

  // DISCOVER_TENANT — resolve one reachable tenant from the stored /me (via
  // useMe), set X-Tenant-Id, and heal the URL — all before any tenant-scoped
  // read. A null tenant means the identity reaches no account — not a usable
  // session (login-time gate: sentinel/internal/login/login.go:43) — so mark the
  // store logged-out, which also clears the snapshot CHECK_SESSION stored.
  const resolveTenant = () => discoverTenant(route, {
    findAccount: doFindAccount,
    accounts: reachableAccounts.value,
    lastActiveAccount: lastActiveAccount.value,
  }).then((tenant) => {
    if (tenant === null) {
      store.commit('USER_NOT_LOGGED_IN');
      return null;
    }

    const account = reachableAccounts.value.find((candidate) => candidate.uid === tenant.accountId);
    if (account?.accessible === false) {
      store.commit('STEP_UP_REQUIRED', { accountId: account.uid, slug: tenant.slug });
      return null;
    }

    store.commit('TENANT_RESOLVED', tenant);

    if (tenant.recoveredFrom && tenant.slug) {
      snackbar.info(t('app.tenantAccessRecovered', { requested: tenant.recoveredFrom, shown: tenant.slug }));
    }

    // start() owns the initial heal; useRouteHealer handles ongoing navigation.
    const target = healTarget(route, tenant.slug, isAuthenticatedRouteName);
    return (target ? router.replace(target).catch(() => {}) : Promise.resolve()).then(() => tenant);
  });

  // userGate — GATE_CHECK onward (tenant resolved, X-Tenant-Id set): fetch the
  // self membership + account, gate the user into onboarding or the activation
  // cycle, then enter ACTIVE (bulk fetch + subscribe + render).
  const userGate = () => preInitializeUser().then((user) => {
    if (user === null) {
      return null;
    }

    const gate = gateDecision({ user: loggedInUser.value, account: loggedInUserAccount.value });

    // Screen gates → onboarding; the bulk fetch waits until onboarding completes
    // and re-enters (demo data is generated when accountInfosCompleted flips, so
    // fetching earlier would load an empty tenant). ACTIVATE is normally
    // screenless, but a configured company-info welcome renders in onboarding
    // too, stamping activatedAt on continue so re-entry lands on ACTIVE (no loop).
    const showsActivationScreen = gate === GATE.ACTIVATE && showCompanyInfoInOnboarding.value;
    if (gate === GATE.PROFILE || gate === GATE.ACCOUNT || showsActivationScreen) {
      EventBus.$emit(EVENTS.SESSION.IDENTIFY, { user: loggedInUser.value, account: loggedInUserAccount.value });
      return router.replace({ name: routeName.onboarding }).then(() => user);
    }

    // ACTIVATE_MEMBERSHIP — screenless stamp before entering; flows into ACTIVE.
    const activation = gate === GATE.ACTIVATE
      ? activateMembership(loggedInUser.value)
      : Promise.resolve();

    // ACTIVE: bulk fetch + subscribe, set the title, restore the last page.
    return activation
      .then(() => initializeUser())
      .then(() => setDocumentTitle(route.meta))
      .then(() => restoreLastPage())
      .then(() => user);
  });

  const start = async () => {
    loading.value = true;
    try {
      const me = await checkSession(); // CHECK_SESSION — session (stored), or ANON → login
      if (me === null) {
        return null;
      }

      const tenant = await resolveTenant(); // DISCOVER_TENANT — one reachable tenant; sets X-Tenant-Id
      if (tenant === null) {
        return null;
      }

      const user = await userGate(); // GATE_CHECK → onboarding / activation → ACTIVE
      return user;
    } finally {
      loading.value = false;
    }
  };

  return { loading, start };
}
